import { produce } from 'immer'
import type {
  IOption,
  MultiSelect,
  SingleSelect,
} from '@user/components/Filters'
import { getNowDateTimeUTC } from '@sales-center/common'
import type { IUser } from '@user/store/authorization'
import { getOrderByFunc } from '@user/components/xGrid'
import { getSaveListViewPreferenceFunc } from '@user/store/userPreferences'

import type {
  ActivitySummaryTab,
  ActivitySummaryFilterSet,
  ActivitySummaryDateFilter,
  ActivitySummaryTopStatsInput,
} from './typings'
import { ActivityOverdue } from './typings'
import { ActivityStatus } from '../constants'
import { clearedFilters, defaultCreatedDate, defaultOrderBy } from './constants'

export class ActivitySummaryHelpers {
  static tabToODataFilter(tab: ActivitySummaryTab): string {
    switch (tab) {
      case ActivityStatus.Todo:
      case ActivityStatus.Deferred:
      case ActivityStatus.Complete:
      case ActivityStatus.InProgress:
        return `(status eq '${tab}')`
      case ActivityOverdue.Overdue: {
        const now = new Date()
        const pastDue = `dueDate le ${getNowDateTimeUTC(now)}`
        const notComplete = `status ne '${ActivityStatus.Complete}'`
        const notDeferred = `status ne '${ActivityStatus.Deferred}'`

        return `(${pastDue} and ${notComplete} and ${notDeferred})`
      }
    }
  }

  static getOrderBy = getOrderByFunc(defaultOrderBy)

  static saveColOrder = getSaveListViewPreferenceFunc('activitySummary')

  static getDefaultFilters = (currentUser: IUser) =>
    produce(clearedFilters, (draft) => {
      draft.filters.createdDate.value = defaultCreatedDate

      draft.filters.ownerId.value = {
        mode: 'any',
        options: [
          {
            value: currentUser.userId,
            label: currentUser.userName,
          },
        ],
      }
    })

  static filterToTopStats(
    filters: ActivitySummaryFilterSet['filters'],
  ): ActivitySummaryTopStatsInput {
    const {
      ownerId,
      dueDate,
      startDate,
      createdDate,
      salesTeamIds,
      salesFunnelId,
      activityTypeId,
    } = filters

    const input: ActivitySummaryTopStatsInput = {
      dueDateFilter: this.daysToStat(dueDate.value),
      startDateFilter: this.daysToStat(startDate.value),
      createdDateFilter: this.daysToStat(createdDate.value),
      salesTeamIds: this.multiSelectToNumberArray(salesTeamIds.value),
      activityOwnerIds: this.multiSelectToStringArray(ownerId.value),
      salesFunnelId: this.singleSelectToNumberArray(salesFunnelId.value),
      activityTypeId: this.singleSelectToNumberArray(activityTypeId.value),
    }

    return input
  }

  private static daysToStat(
    option: IOption | null,
  ): ActivitySummaryDateFilter | undefined {
    const days = option?.value

    switch (days) {
      case -7:
        return 'Last7Days'
      case -30:
        return 'Last30Days'
      case -90:
        return 'Last90Days'
    }
  }

  private static singleSelectToNumberArray(value: SingleSelect) {
    if (value?.value && value.value !== null) {
      return Number(value.value)
    }
  }

  private static multiSelectToNumberArray(value: MultiSelect) {
    const { options } = value

    const result = options.reduce((acc, curr) => {
      if (typeof curr.value === 'number') return [...acc, curr.value]
      return acc
    }, [])

    if (result.length > 0) return result
  }

  private static multiSelectToStringArray(value: MultiSelect) {
    const { options } = value

    const result = options.reduce((acc, curr) => {
      if (typeof curr.value === 'string' || curr.value === null)
        return [...acc, curr.value]
      return acc
    }, [])

    if (result.length > 0) return result
  }
}
